import { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { X, Save, FileText, Zap, Target, Brain } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Slider } from '@/components/ui/slider';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Breadcrumb } from '@/components/ui/breadcrumb';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import type { CollectionStrategyItem, StrategyAction } from '@/types/collection-strategies';
import { Plus, Trash2, Edit } from 'lucide-react';
import { ActionEditorDialog } from '@/components/settings/action-editor-dialog';

export function StrategyEditorPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();

    // Get strategy and mode from location state or URL params
    const strategy = location.state?.strategy as CollectionStrategyItem | null;
    const mode = (searchParams.get('mode') || location.state?.mode || 'create') as 'create' | 'edit';

    const [formData, setFormData] = useState({
        name: '',
        type: 'Standard' as CollectionStrategyItem['type'],
        description: '',
        status: 'Active' as 'Active' | 'Inactive',
        triggerType: 'days_overdue' as any,
        daysOverdue: 30,
        amountThreshold: 5000,
        riskScoreThreshold: 70,
        actions: [] as any[],
        assignmentRules: [] as any[],
        aiEnabled: false,
        aiTone: 3,
        aiFormality: 'professional' as 'casual' | 'professional' | 'formal',
        aiPersonalization: 'moderate' as 'minimal' | 'moderate' | 'deep',
        aiContextFactors: {
            paymentHistory: true,
            riskProfile: true,
            relationshipDuration: false,
            industryContext: false,
            economicFactors: false,
            previousDisputes: false,
            communicationPreferences: false,
        },
        aiTimingOptimization: true,
    });

    const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = useState(false);
    const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
    const [showActionEditor, setShowActionEditor] = useState(false);
    const [editingAction, setEditingAction] = useState<StrategyAction | null>(null);

    // Initialize form data when strategy changes
    useEffect(() => {
        if (strategy && mode === 'edit') {
            setFormData({
                name: strategy.name,
                type: strategy.type,
                description: strategy.description,
                status: strategy.status,
                triggerType: strategy.trigger.type,
                daysOverdue: strategy.trigger.daysOverdue || 30,
                amountThreshold: strategy.trigger.amountThreshold || 5000,
                riskScoreThreshold: strategy.trigger.riskScoreThreshold || 70,
                actions: strategy.actions,
                assignmentRules: strategy.assignmentRules,
                aiEnabled: !!strategy.aiBehavior?.enabled,
                aiTone: strategy.aiBehavior?.communicationTone || 3,
                aiFormality: strategy.aiBehavior?.formalityLevel || 'professional',
                aiPersonalization: strategy.aiBehavior?.personalizationDepth || 'moderate',
                aiContextFactors: strategy.aiBehavior?.contextFactors || {
                    paymentHistory: true,
                    riskProfile: true,
                    relationshipDuration: false,
                    industryContext: false,
                    economicFactors: false,
                    previousDisputes: false,
                    communicationPreferences: false,
                },
                aiTimingOptimization: strategy.aiBehavior?.timingOptimization || true,
            });
        } else {
            setFormData({
                name: '',
                type: 'Standard',
                description: '',
                status: 'Active',
                triggerType: 'days_overdue',
                daysOverdue: 30,
                amountThreshold: 5000,
                riskScoreThreshold: 70,
                actions: [],
                assignmentRules: [],
                aiEnabled: false,
                aiTone: 3,
                aiFormality: 'professional',
                aiPersonalization: 'moderate',
                aiContextFactors: {
                    paymentHistory: true,
                    riskProfile: true,
                    relationshipDuration: false,
                    industryContext: false,
                    economicFactors: false,
                    previousDisputes: false,
                    communicationPreferences: false,
                },
                aiTimingOptimization: true,
            });
        }
    }, [strategy, mode]);

    // Check if there are unsaved changes
    const hasUnsavedChanges = () => {
        if (mode === 'create') {
            return formData.name !== '' || formData.description !== '';
        }
        if (strategy) {
            return (
                formData.name !== strategy.name ||
                formData.type !== strategy.type ||
                formData.description !== strategy.description ||
                formData.status !== strategy.status ||
                formData.triggerType !== strategy.trigger.type ||
                formData.daysOverdue !== (strategy.trigger.daysOverdue || 30) ||
                formData.amountThreshold !== (strategy.trigger.amountThreshold || 5000) ||
                formData.riskScoreThreshold !== (strategy.trigger.riskScoreThreshold || 70)
            );
        }
        return false;
    };

    const handleSave = () => {
        // Basic validation
        if (!formData.name.trim()) {
            toast.error('Please enter a strategy name');
            return;
        }
        if (!formData.description.trim()) {
            toast.error('Please enter a strategy description');
            return;
        }

        toast.success(mode === 'create' ? 'Strategy created successfully' : 'Strategy updated successfully');
        navigate('/settings/strategies');
    };

    const handleCancel = () => {
        if (hasUnsavedChanges()) {
            setPendingNavigation('/settings/strategies');
            setShowUnsavedChangesDialog(true);
        } else {
            navigate('/settings/strategies');
        }
    };

    const confirmNavigation = () => {
        if (pendingNavigation) {
            navigate(pendingNavigation);
        }
        setShowUnsavedChangesDialog(false);
        setPendingNavigation(null);
    };

    const cancelNavigation = () => {
        setShowUnsavedChangesDialog(false);
        setPendingNavigation(null);
    };

    const handleAddAction = () => {
        setEditingAction(null);
        setShowActionEditor(true);
    };

    const handleEditAction = (action: StrategyAction) => {
        setEditingAction(action);
        setShowActionEditor(true);
    };

    const handleDeleteAction = (actionId: string) => {
        setFormData({
            ...formData,
            actions: formData.actions.filter(a => a.id !== actionId),
        });
        toast.success('Action removed successfully');
    };

    const handleSaveAction = (action: StrategyAction) => {
        if (editingAction) {
            // Update existing action
            setFormData({
                ...formData,
                actions: formData.actions.map(a => a.id === action.id ? action : a),
            });
        } else {
            // Add new action
            setFormData({
                ...formData,
                actions: [...formData.actions, action],
            });
        }
        setShowActionEditor(false);
        setEditingAction(null);
    };

    const getActionTimingLabel = (action: StrategyAction): string => {
        if (action.timing.type === 'days_from_trigger') {
            return `Day ${action.timing.daysFromTrigger} from trigger`;
        } else if (action.timing.type === 'days_from_previous') {
            return `${action.timing.daysFromPrevious} days after previous action`;
        } else {
            return 'Conditional';
        }
    };

    const getActionTypeLabel = (type: StrategyAction['type']): string => {
        const labels: Record<StrategyAction['type'], string> = {
            email: 'Send Email',
            phone_call: 'Phone Call',
            payment_link: 'Send Payment Link',
            sms: 'Send SMS',
            escalation: 'Escalate Case',
            account_action: 'Account Action',
            payment_plan: 'Offer Payment Plan',
        };
        return labels[type] || type;
    };

    const breadcrumbItems = [
        { label: 'Settings', href: '/settings/strategies' },
        { label: 'Collection Strategies', href: '/settings/strategies' },
        { label: mode === 'create' ? 'Create Strategy' : 'Edit Strategy' },
    ];

    return (
        <div className="flex-1 flex flex-col h-screen overflow-hidden">
            {/* Header with Breadcrumb */}
            <div className="border-b bg-background px-8 py-4">
                <div className="flex items-center justify-between mb-3">
                    <Breadcrumb items={breadcrumbItems} />
                </div>
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold tracking-tight">
                            {mode === 'create' ? 'Create Strategy' : `Edit Strategy: ${strategy?.name}`}
                        </h1>
                        <p className="text-muted-foreground mt-1">
                            {mode === 'create'
                                ? 'Create a new collection strategy with automated workflows'
                                : 'Modify the strategy settings and configuration'
                            }
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button variant="outline" onClick={handleCancel}>
                            <X className="h-4 w-4 mr-2" />
                            Cancel
                        </Button>
                        <Button onClick={handleSave} disabled={!formData.name || !formData.description}>
                            <Save className="h-4 w-4 mr-2" />
                            {mode === 'create' ? 'Create Strategy' : 'Save Changes'}
                        </Button>
                    </div>
                </div>
            </div>

            {/* Main Content - Panels Layout */}
            <div className="flex-1 overflow-y-auto px-8 py-6">
                <div className="max-w-7xl mx-auto space-y-6">
                    {/* Row 1: Overview and Triggers Side-by-Side */}
                    <div className="grid grid-cols-2 gap-6">
                        {/* Panel 1: Overview */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <FileText className="h-5 w-5" />
                                    Overview
                                </CardTitle>
                                <CardDescription>
                                    Basic information about the collection strategy
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div>
                                    <Label>Strategy name *</Label>
                                    <Input
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        placeholder="e.g., Standard 30-Day Collection"
                                    />
                                </div>
                                <div>
                                    <Label>Type *</Label>
                                    <Select
                                        value={formData.type}
                                        onValueChange={(value: any) => setFormData({ ...formData, type: value })}
                                    >
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="Standard">Standard</SelectItem>
                                            <SelectItem value="Intensive">Intensive</SelectItem>
                                            <SelectItem value="VIP">VIP</SelectItem>
                                            <SelectItem value="High Risk">High Risk</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label>Description *</Label>
                                    <Textarea
                                        value={formData.description}
                                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                        placeholder="Describe when and how this strategy should be used..."
                                        rows={4}
                                    />
                                </div>
                            </CardContent>
                        </Card>

                        {/* Panel 2: Triggers */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Zap className="h-5 w-5" />
                                    Triggers
                                </CardTitle>
                                <CardDescription>
                                    Define when this strategy should be automatically applied
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div>
                                    <Label>Trigger Type</Label>
                                    <Select
                                        value={formData.triggerType}
                                        onValueChange={(value: any) => setFormData({ ...formData, triggerType: value })}
                                    >
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="days_overdue">Days Overdue</SelectItem>
                                            <SelectItem value="amount_threshold">Amount Threshold</SelectItem>
                                            <SelectItem value="risk_score">Risk Score</SelectItem>
                                            <SelectItem value="manual_only">Manual Only</SelectItem>
                                            <SelectItem value="payment_pattern_change">Payment Pattern Change</SelectItem>
                                            <SelectItem value="promise_broken">Promise Broken</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                {formData.triggerType === 'days_overdue' && (
                                    <div>
                                        <Label>Days Overdue Threshold</Label>
                                        <Input
                                            type="number"
                                            value={formData.daysOverdue}
                                            onChange={(e) => setFormData({ ...formData, daysOverdue: parseInt(e.target.value) || 0 })}
                                        />
                                        <p className="text-sm text-muted-foreground mt-1">
                                            Trigger when invoice is overdue by this many days
                                        </p>
                                    </div>
                                )}

                                {formData.triggerType === 'amount_threshold' && (
                                    <div>
                                        <Label>Amount Threshold ($)</Label>
                                        <Input
                                            type="number"
                                            value={formData.amountThreshold}
                                            onChange={(e) => setFormData({ ...formData, amountThreshold: parseInt(e.target.value) || 0 })}
                                        />
                                        <p className="text-sm text-muted-foreground mt-1">
                                            Trigger when outstanding amount exceeds this value
                                        </p>
                                    </div>
                                )}

                                {formData.triggerType === 'risk_score' && (
                                    <div>
                                        <Label>Risk Score Threshold</Label>
                                        <Input
                                            type="number"
                                            min="0"
                                            max="100"
                                            value={formData.riskScoreThreshold}
                                            onChange={(e) => {
                                                const value = parseInt(e.target.value) || 0;
                                                setFormData({ ...formData, riskScoreThreshold: Math.min(100, Math.max(0, value)) });
                                            }}
                                        />
                                        <p className="text-sm text-muted-foreground mt-1">
                                            Trigger when customer risk score reaches or exceeds this value (0-100)
                                        </p>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>

                    {/* Panel 3: Actions */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Target className="h-5 w-5" />
                                Actions
                            </CardTitle>
                            <CardDescription>
                                Configure the collection actions to be performed
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="flex items-center justify-between">
                                <Label>Collection Actions</Label>
                                <Button size="sm" variant="outline" onClick={handleAddAction}>
                                    <Plus className="h-4 w-4 mr-2" />
                                    Add Action
                                </Button>
                            </div>
                            {formData.actions.length === 0 ? (
                                <div className="border rounded-lg p-8 text-center">
                                    <p className="text-muted-foreground">No actions configured yet</p>
                                    <p className="text-sm text-muted-foreground mt-1">
                                        Click "Add Action" to create your first collection action
                                    </p>
                                </div>
                            ) : (
                                <div className="border rounded-lg p-4 space-y-3">
                                    {formData.actions
                                        .sort((a, b) => a.order - b.order)
                                        .map((action) => (
                                            <div key={action.id} className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                                                <div className="flex-1">
                                                    <div className="font-medium">
                                                        {getActionTypeLabel(action.type)}
                                                        {action.templateName && (
                                                            <span className="text-sm text-muted-foreground ml-2">
                                                                ({action.templateName})
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="text-sm text-muted-foreground">
                                                        {getActionTimingLabel(action)}
                                                    </div>
                                                    {action.details && (
                                                        <div className="text-sm text-muted-foreground mt-1">
                                                            {action.details}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        onClick={() => handleEditAction(action)}
                                                    >
                                                        <Edit className="h-4 w-4" />
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        onClick={() => handleDeleteAction(action.id)}
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                        ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Panel 4: AI Behavior */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Brain className="h-5 w-5" />
                                AI Behavior
                            </CardTitle>
                            <CardDescription>
                                Configure AI-powered communication and decision-making
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div>
                                <Label>Communication Tone</Label>
                                <div className="space-y-2 mt-2">
                                    <Slider
                                        value={[formData.aiTone]}
                                        onValueChange={(value) => setFormData({ ...formData, aiTone: value[0] })}
                                        min={1}
                                        max={5}
                                        step={1}
                                        className="w-full"
                                    />
                                    <div className="flex justify-between text-xs text-muted-foreground">
                                        <span>Highly Empathetic</span>
                                        <span>Balanced</span>
                                        <span>Firm Results</span>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <Label>Formality Level</Label>
                                <Select
                                    value={formData.aiFormality}
                                    onValueChange={(value: any) => setFormData({ ...formData, aiFormality: value })}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="casual">Casual</SelectItem>
                                        <SelectItem value="professional">Professional</SelectItem>
                                        <SelectItem value="formal">Formal</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <div>
                                <Label>Context Factors</Label>
                                <div className="space-y-2 mt-2">
                                    <div className="flex items-center gap-2">
                                        <Checkbox
                                            id="payment-history"
                                            checked={formData.aiContextFactors.paymentHistory}
                                            onCheckedChange={(checked) =>
                                                setFormData({
                                                    ...formData,
                                                    aiContextFactors: { ...formData.aiContextFactors, paymentHistory: !!checked }
                                                })
                                            }
                                        />
                                        <Label htmlFor="payment-history" className="cursor-pointer">
                                            Payment History
                                        </Label>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Checkbox
                                            id="risk-profile-ai"
                                            checked={formData.aiContextFactors.riskProfile}
                                            onCheckedChange={(checked) =>
                                                setFormData({
                                                    ...formData,
                                                    aiContextFactors: { ...formData.aiContextFactors, riskProfile: !!checked }
                                                })
                                            }
                                        />
                                        <Label htmlFor="risk-profile-ai" className="cursor-pointer">
                                            Risk Profile
                                        </Label>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Checkbox
                                            id="relationship-length"
                                            checked={formData.aiContextFactors.relationshipDuration}
                                            onCheckedChange={(checked) =>
                                                setFormData({
                                                    ...formData,
                                                    aiContextFactors: { ...formData.aiContextFactors, relationshipDuration: !!checked }
                                                })
                                            }
                                        />
                                        <Label htmlFor="relationship-length" className="cursor-pointer">
                                            Relationship Length
                                        </Label>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Checkbox
                                            id="previous-disputes"
                                            checked={formData.aiContextFactors.previousDisputes}
                                            onCheckedChange={(checked) =>
                                                setFormData({
                                                    ...formData,
                                                    aiContextFactors: { ...formData.aiContextFactors, previousDisputes: !!checked }
                                                })
                                            }
                                        />
                                        <Label htmlFor="previous-disputes" className="cursor-pointer">
                                            Previous Disputes
                                        </Label>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Unsaved Changes Dialog */}
            <AlertDialog open={showUnsavedChangesDialog} onOpenChange={setShowUnsavedChangesDialog}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
                        <AlertDialogDescription>
                            You have unsaved changes. Are you sure you want to leave? Your changes will be lost.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel onClick={cancelNavigation}>Stay on Page</AlertDialogCancel>
                        <AlertDialogAction onClick={confirmNavigation}>Leave Without Saving</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Action Editor Dialog */}
            <ActionEditorDialog
                open={showActionEditor}
                onClose={() => {
                    setShowActionEditor(false);
                    setEditingAction(null);
                }}
                onSave={handleSaveAction}
                initialData={editingAction}
                existingActions={formData.actions}
            />
        </div>
    );
}

export default StrategyEditorPage;
